import { useEffect, useState } from 'react';

import { useGetCurrentMonthStats } from 'src/api/admin';

import { DashboardPeriod } from 'src/types';

import { servedPeriodOf } from './period';

/**
 * NAT-2334 / NAT-2545 — the Termometro's figures for a selected period, and what the page may
 * honestly say about them. Shared by Operativa and «Situazione attuale», which both put a period
 * toggle above the same eight cards: the rules for "is this the span that was asked for" have to
 * be one rule, or the two pages will disagree about the same payload.
 */
export function useTermometroPeriod(period: DashboardPeriod) {
  const { data, error, isValidating } = useGetCurrentMonthStats(period);

  // The span actually served, normalised in ONE place (`servedPeriodOf`) so the page and the
  // cards cannot disagree about what an absent `period` means. Comparing the raw field would
  // make `undefined !== 'mtd'` true and flag every background revalidation against a backend
  // that predates the toggle.
  const servedPeriod = servedPeriodOf(data);

  // The period that was SELECTED when the last response settled — sticky, and deliberately not
  // derived from `isValidating`. SWR revalidates on window focus and retries errors on a backoff
  // forever, and every one of those flips `isValidating`; deriving "settled" from it made the
  // warning unmount and remount on each, jumping the cards and re-announcing it, with no user
  // action at all.
  const [settledPeriod, setSettledPeriod] = useState<DashboardPeriod | null>(null);
  useEffect(() => {
    if (!isValidating && (data || error)) setSettledPeriod(period);
  }, [isValidating, data, error, period]);

  // In flight towards a different span: the figures on screen are the previous span's, labelled
  // by their own caption, so they stay put while the new ones load.
  const isSwitching = isValidating && settledPeriod !== period;
  // The FIRST load needs its own condition. `servedPeriodOf(undefined)` is 'mtd' and the default
  // selection is also 'mtd', so `isSwitching` is false on the commonest in-flight case there is.
  const isFirstLoad = isValidating && !data;
  // Deliberately NOT true for a background revalidation that changes nothing.
  const isBusy = isSwitching || isFirstLoad;

  // SETTLED on a span other than the one asked for. The request failed — a backend that predates
  // `l12m` answers 400, and `keepPreviousData` leaves the previous span's figures in place — or
  // the backend does not know `?period=` at all and served a month. Either way the figures on
  // screen are not the selected period's, and must not read as if they were.
  const periodUnavailable = Boolean(data) && settledPeriod === period && servedPeriod !== period;

  return { data, error, servedPeriod, isBusy, periodUnavailable };
}
